/**
 * Minimal, safe markdown for agent replies: headings, paragraphs, lists,
 * tables, code, bold, italics, links. Builds React elements only (no HTML
 * injection); links must be http(s).
 */
import React from 'react';

const LIST_ITEM = /^\s*([-*]|\d+[.)])\s+/;
const SAFE_URL = /^https?:\/\/[^\s]+$/i;
const INLINE = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*([^*\s][^*]*)\*)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])/;

const splitRow = (row) =>
  row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

const startsBlock = (line) => /^#{1,4}\s/.test(line) || LIST_ITEM.test(line) || line.trim().startsWith('|') || /^```/.test(line);

export function parseBlocks(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i += 1;
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (/^```/.test(line)) {
      const code = [];
      i += 1;
      while (i < lines.length && !/^```/.test(lines[i])) code.push(lines[(i += 1) - 1]);
      i += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items = [];
      while (i < lines.length && LIST_ITEM.test(lines[i]) && /^\s*\d+[.)]\s+/.test(lines[i]) === ordered) {
        items.push(lines[i].replace(LIST_ITEM, ''));
        i += 1;
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      continue;
    }

    const paragraph = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !startsBlock(lines[i])) {
      paragraph.push(lines[i]);
      i += 1;
    }
    blocks.push({ type: 'paragraph', lines: paragraph });
  }
  return blocks;
}

export function renderInline(text, keyPrefix = 'i') {
  const nodes = [];
  let rest = String(text || '');
  let index = 0;
  while (rest.length) {
    const match = INLINE.exec(rest);
    if (!match) {
      nodes.push(rest);
      break;
    }
    if (match.index > 0) nodes.push(rest.slice(0, match.index));
    const key = `${keyPrefix}-${index}`;
    index += 1;
    if (match[1]) {
      nodes.push(<strong key={key}>{renderInline(match[2], key)}</strong>);
    } else if (match[3]) {
      nodes.push(
        <code key={key} className="dagent-md__code">
          {match[4]}
        </code>,
      );
    } else if (match[5]) {
      nodes.push(
        SAFE_URL.test(match[7]) ? (
          <a key={key} href={match[7]} target="_blank" rel="noopener noreferrer">
            {match[6]}
          </a>
        ) : (
          match[6]
        ),
      );
    } else if (match[8]) {
      nodes.push(<em key={key}>{match[9]}</em>);
    } else if (match[10]) {
      nodes.push(
        <a key={key} href={match[10]} target="_blank" rel="noopener noreferrer">
          {match[10]}
        </a>,
      );
    }
    rest = rest.slice(match.index + match[0].length);
  }
  return nodes;
}

function MarkdownText({ text, className = '' }) {
  const blocks = parseBlocks(text);
  return (
    <div className={`dagent-md ${className}`}>
      {blocks.map((block, blockIndex) => {
        const key = `b${blockIndex}`;
        if (block.type === 'heading') {
          const Tag = block.level <= 2 ? 'h4' : 'h5';
          return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
        }
        if (block.type === 'code') {
          return (
            <pre key={key} className="dagent-md__pre">
              {block.text}
            </pre>
          );
        }
        if (block.type === 'table') {
          return (
            <div key={key} className="dagent-table-wrap">
              <table className="dagent-table">
                <thead>
                  <tr>
                    {block.header.map((cell, cellIndex) => (
                      <th key={cellIndex}>{renderInline(cell, `${key}h${cellIndex}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {block.header.map((_, cellIndex) => (
                        <td key={cellIndex}>{renderInline(row[cellIndex] || '', `${key}r${rowIndex}c${cellIndex}`)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          const Tag = block.type;
          return (
            <Tag key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `${key}l${itemIndex}`)}</li>
              ))}
            </Tag>
          );
        }
        return (
          <p key={key}>
            {block.lines.map((line, lineIndex) => (
              <React.Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `${key}p${lineIndex}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

export default MarkdownText;
